import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FileRemoveEvent, FileSelectEvent, FileUpload, FileUploadErrorEvent, FileUploadEvent } from 'primeng/fileupload';
import { Subject, Subscription } from 'rxjs';
import { InfoReportService } from 'src/app/services/info-report.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AppUtils } from 'src/app/shared/app.utils';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'ness-summary-notes',
  templateUrl: './summary-notes.component.html'
})
export class SummaryNotesComponent implements OnInit {
  apiRoot: string = environment.apiEndpoint;
  attachmentUploadErrorMessage: string;
  displayAttachmentErrorMessage: boolean = false;
  @ViewChild('downloadTemplateLink', { static: false }) private downloadTemplateLink: ElementRef;
  @ViewChild('fileInput', { static: false }) fileInput: FileUpload;
  maxFileSize: number = environment.maxUploadFileSizeInBytes; //5mb
  @Output() nsSubmitted = new EventEmitter<any>();
  rows: number = AppConstants.defaultTableRows;
  sentDatesForm: FormGroup;
  summaryNotesData: any;
  summaryNotesForm: FormGroup;
  textAreaRows: number = AppConstants.defaultTextAreaRows;
  totalNumberOfUploadFilesAllowed: number = AppConstants.totalNumberOfUploadFilesAllowed;

  private _infoReportUpdatedSub$: Subscription;
  private _unsubscribe$: Subject<void> = new Subject<void>();
  private _uploadedFiles: ServerModels.ENESS_IntelligenceReportAttachments[] = [];
  get uploadedFiles(): ServerModels.ENESS_IntelligenceReportAttachments[] {
    return this._uploadedFiles;
  }

  get ENESS_XREF_IRsDatesSent(): FormArray {
    return this.summaryNotesForm.get('ENESS_XREF_IRsDatesSent') as FormArray;
  }

  constructor(private fb: FormBuilder,
    private infoReportService: InfoReportService,
    public elementRef: ElementRef) {

    this.summaryNotesForm = this.fb.group({
      IntelligenceReportID: '',
      Summary: ['', Validators.maxLength(4000)],
      Notes: '',
      ENESS_XREF_IRsAttachments: this.fb.array([]),
      ENESS_XREF_IRsDatesSent: this.fb.array([])
    });

    this._infoReportUpdatedSub$ = this.infoReportService.infoReportUpdated$.subscribe((updatedIR) => this._updateFormData(updatedIR));
  }

  ngOnInit() {

    this.sentDatesForm = this.fb.group({
      IRDateSentID: null,
      IntelligenceReportID: null,
      DateSent: null,
      Recipients: [{ value: '', disabled: true }, Validators.required]
    });
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this._infoReportUpdatedSub$.unsubscribe();
  }

  private _updateFormData(ir: ServerModels.ENESS_IntelligenceReports) {
    if (this.summaryNotesForm) {
      ir.ENESS_XREF_IRsDatesSent.forEach(ds => {
        let sent = this.fb.group({
          IRDateSentID: ds.IRDateSentID,
          IntelligenceReportID: ds.IntelligenceReportID,
          DateSent: ds.DateSent,
          Recipients: ds.Recipients
        });
        (<FormArray>this.summaryNotesForm.get('ENESS_XREF_IRsDatesSent')).push(sent);
      });

      ir.ENESS_XREF_IRsAttachments.forEach(a => {
        let att = this.fb.group({
          IRAttachmentID: a.IRAttachmentID,
          IntelligenceReportID: a.IntelligenceReportID,
          IntelligenceReportAttachmentID: a.IntelligenceReportAttachmentID,
          ENESS_IntelligenceReportAttachments: this.fb.group({
            FileName: a.ENESS_IntelligenceReportAttachments.FileName,
            IntelligenceReportAttachmentID: a.ENESS_IntelligenceReportAttachments.IntelligenceReportAttachmentID,
            CreatedBy: a.ENESS_IntelligenceReportAttachments.CreatedBy,
            CreatedById: a.ENESS_IntelligenceReportAttachments.CreatedById
          })
        });
        (<FormArray>this.summaryNotesForm.get('ENESS_XREF_IRsAttachments')).push(att);
        this._uploadedFiles.push(a.ENESS_IntelligenceReportAttachments);
      });

      this.summaryNotesForm.patchValue({
        IntelligenceReportID: ir.IntelligenceReportID,
        Summary: ir.Summary,
        Notes: ir.Notes
      });
    }
  }

  onDateSentBlur() {
    if (this.sentDatesForm.get('DateSent').value) {
      this.sentDatesForm.get('Recipients').enable();
    } else {
      this.sentDatesForm.get('Recipients').setValue(null);
      this.sentDatesForm.get('Recipients').disable();
    }
  }

  addIRSentDate() {
    const newSentDate = {
      DateSent: this.sentDatesForm.value.DateSent,
      Recipients: this.sentDatesForm.value.Recipients
    };
    let fa = this.summaryNotesForm.get('ENESS_XREF_IRsDatesSent') as FormArray;
    fa.push(this.fb.group(newSentDate));
    this.sentDatesForm.reset({
      Recipients: { value: '', disabled: true }
    });
  }

  onUploadErrorHandler(event: FileUploadErrorEvent) {
    this.attachmentUploadErrorMessage = event.error.error || event.error.message;;
    this.displayAttachmentErrorMessage = true;
    this.fileInput.msgs = [];
    this.fileInput.msgs.push({
      severity: 'error',
      summary: 'Attachmend Upload Error:',
      detail: this.attachmentUploadErrorMessage
    });
  }

  onFileUpload(event: FileUploadEvent) {
    if (event.originalEvent['status'] !== 200) {
      return;
    }

    const response = event.originalEvent['body'];
    if (response) {
      const formData = this.summaryNotesForm.value;
      for (let i = 0; i < response.length; i++) {
        if (response[i].CreatedOn) {
          response[i].CreatedOn = new Date(parseInt(response[i].CreatedOn.substr(6)));
        }
      }
      this._uploadedFiles = [...<ServerModels.ENESS_IntelligenceReportAttachments[]>response, ...this._uploadedFiles];

      this._uploadedFiles.forEach(x => {
        if (!(<ServerModels.ENESS_XREF_IRsAttachments[]>formData.ENESS_XREF_IRsAttachments).find(y => y.IntelligenceReportAttachmentID.toLowerCase() == x.IntelligenceReportAttachmentID)) {
          formData.ENESS_XREF_IRsAttachments.push({
            IRAttachmentID: null,
            IntelligenceReportID: formData.IntelligenceReportID,
            IntelligenceReportAttachmentID: x.IntelligenceReportAttachmentID
          });
        }
      });

      this.summaryNotesData = {
        formData: formData,
        tab: 'notesSummary'
      };

      this.nsSubmitted.emit(this.summaryNotesData);
    }
  }

  deleteUploadedAttachment(item: ServerModels.ENESS_IntelligenceReportAttachments) {
    item.Deleted = true;
    this.infoReportService.deleteInformationReportAttachment(item).subscribe({
      next: (result: number) => {
        const formData = this.summaryNotesForm.value;
        let index = this.uploadedFiles.indexOf(item);
        if (index > -1) {
          this.uploadedFiles.splice(index, 1);
        }
        for (let i = 0; i < (<ServerModels.ENESS_XREF_IRsAttachments[]>formData.ENESS_XREF_IRsAttachments).length; i++) {
          if ((<ServerModels.ENESS_XREF_IRsAttachments[]>formData.ENESS_XREF_IRsAttachments)[i].IntelligenceReportAttachmentID == item.IntelligenceReportAttachmentID) {
            formData.ENESS_XREF_IRsAttachments.splice(i, 1);
          }
        }
      }
    });
  }

  onSubmit() {
    if (this.summaryNotesForm.valid) {
      this._uploadAttachments();
    }
  }

  onClickDownload(file: ServerModels.ENESS_IntelligenceReportAttachments) {
    if (file) {
      const blob = AppUtils.base64toBlob(file.Attachment, null);
      const link = this.downloadTemplateLink.nativeElement;
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = file.FileName;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  }

  public validateMaxFiles(evt: FileSelectEvent | FileRemoveEvent): void {
    if (Object.hasOwn(evt, 'files')) {
      this.displayAttachmentErrorMessage = (this.fileInput.files.length + this._uploadedFiles.length) > this.totalNumberOfUploadFilesAllowed;
    }

    if (Object.hasOwn(evt, 'file')) {
      this.displayAttachmentErrorMessage = (this.fileInput.files.length - 1 + this._uploadedFiles.length) > this.totalNumberOfUploadFilesAllowed;
    }

    if (this.displayAttachmentErrorMessage) {
      this.attachmentUploadErrorMessage = "Total number of uploaded files exceed the max limit.";
    }
  }

  public OnCancel(): void {
    this.displayAttachmentErrorMessage = this._uploadedFiles.length > this.totalNumberOfUploadFilesAllowed;

    if (this.displayAttachmentErrorMessage) {
      this.attachmentUploadErrorMessage = "Total number of uploaded files exceed the max limit.";
    }
  }

  private _uploadAttachments() {
    if (this.fileInput.files.length > 0) {
      this.fileInput.upload();
    } else {
      const formData = this.summaryNotesForm.value;
      this.summaryNotesData = {
        formData: formData,
        tab: 'notesSummary'
      };
      this.nsSubmitted.emit(this.summaryNotesData);
    }
  }
}
